import type { DesignTypeCategories } from "@nlc/shared-types";

import { Controller } from "react-hook-form";

import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
  ComboBoxSection,
} from "@/components/combobox";
import { FieldError, Label } from "@/components/field";
import {
  SelectListItemDescription,
  SelectListItemLabel,
} from "@/components/select";
import { Skeleton } from "@/components/skeleton";
import { Text } from "@/components/text";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useGetDesignTypes } from "@/features/request/edit/hooks/useGetDesignTypes";

const CATEGORY_MAP = {
  graphic: "graphic",
  video: "video",
  webui: "website",
};

export function SelectRequestType({
  category,
  isDisabled,
}: {
  category: DesignTypeCategories;
  isDisabled?: boolean;
}) {
  const { control, setValue } = useEditRequestFormContext();
  const { updateField } = useFieldUpdate();
  const { data, isFetching } = useGetDesignTypes();

  // Mirrors the backend, which resets the level when the design type changes.
  const syncDefaultComplexity = (designTypeId: unknown) => {
    const defaultComplexityId = data?.subcategories
      .flatMap((subcategory) => subcategory.designTypes)
      .find((type) => type.id === designTypeId)?.defaultComplexityId;

    if (defaultComplexityId) setValue("complexityId", defaultComplexityId);
  };

  if (isFetching) {
    return (
      <div className="flex flex-col gap-sm">
        <Label requiredHint>Design type</Label>
        <Skeleton className="h-[42px] w-full rounded-md" />
      </div>
    );
  }

  return (
    <Controller
      control={control}
      name="designTypeId"
      render={({
        field: { name, onBlur, onChange, ref, value },
        fieldState: { error, invalid },
      }) => (
        <ComboBox
          allowsEmptyCollection
          aria-label={CATEGORY_MAP[category] + " type"}
          isDisabled={isDisabled}
          isInvalid={invalid}
          menuTrigger="input"
          name={name}
          onBlur={onBlur}
          onSelectionChange={(id) => {
            if (id !== value) {
              onChange(id);
              void updateField(name, id, () => syncDefaultComplexity(id));
            }
          }}
          selectedKey={value}
        >
          <Label requiredHint>Design type</Label>
          <ComboBoxGroup>
            <ComboBoxInput
              placeholder={`Select ${CATEGORY_MAP[category]} Type`}
              ref={ref}
            />
            <ComboBoxButton />
          </ComboBoxGroup>
          <FieldError>{error?.message}</FieldError>

          <ComboBoxPopover>
            <ComboBoxListBox
              aria-label="Design type"
              className="max-h-[291px]"
              renderEmptyState={() => (
                <Text className="p-2">No results found</Text>
              )}
            >
              {data?.subcategories?.map((subcategory) => (
                <ComboBoxSection
                  aria-label={subcategory.name}
                  key={subcategory.name}
                  title={subcategory.name}
                >
                  {subcategory.designTypes.map((type) => {
                    if (!type.description) {
                      return (
                        <ComboBoxListItem id={type.id} key={type.name}>
                          {type.name}
                        </ComboBoxListItem>
                      );
                    }

                    return (
                      <ComboBoxListItem
                        id={type.id}
                        key={type.id}
                        textValue={type.name}
                      >
                        <SelectListItemLabel aria-label={type.name}>
                          {type.name}
                        </SelectListItemLabel>
                        <SelectListItemDescription
                          aria-label={type.description}
                          className="block w-full"
                        >
                          {type.description}
                        </SelectListItemDescription>
                      </ComboBoxListItem>
                    );
                  })}
                </ComboBoxSection>
              ))}
            </ComboBoxListBox>
          </ComboBoxPopover>
        </ComboBox>
      )}
    />
  );
}
